'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, ChevronRight, Clock, Minus, Plus, UserRound } from 'lucide-react';
import {
  ResponsiveSheet,
  SheetPage,
  useResponsiveSheet,
} from '@church/nextjs-ui/components/ResponsiveSheet';
import type {
  ClientEvent,
  ClientForm,
  ClientPerson,
  ClientProduct,
  EventShift,
  GatheringDoor,
  MailingAddress,
  MedicalDraft,
  MedicalItem,
  MedicalType,
  PromoLine,
  RegistrantDraft,
  SheetDress,
  SubmitResult,
} from '@/lib/types';
import { isVisible } from '@church/nextjs-ui/website/forms';
import type { FormCampusOption } from '@church/nextjs-ui/website/forms';
import { checkPromo, errorText, payInvoice, previewPromos, submitRegistration } from '@/lib/api';
import { showsShifts, offersBack, doorFor } from '@/lib/shift-screen';
import { EventSheetBand } from './EventSheetBand';
import { popupPaymentsWanted } from '@/lib/payment';
import { PaymentPanel, usePaymentWindow } from './PaymentWindow';
import { SeriesNote } from './OtherDates';
import { priceRegistration, type PromoApplied } from '@/lib/pricing';
import { fmtMoney, formatPhone, listNames } from '@/lib/format';
import { Avatar } from './Avatar';
import { RegistrationTypes, ShiftPicker, trimShared } from './RegistrationTypes';
import { Questions } from './Questions';
import { AddressFields } from './AddressFields';
import { addressMissing, EMPTY_ADDRESS } from '@/lib/address';
import { SignStep, type WaiverGroup } from './Waivers';
import { emergencyFieldsOf, waiverOf } from '@/lib/questions';
import type { LearnedRelationship } from '@/lib/relationships';
import { seedAnswers } from '@/lib/questions';
import { isDiscountShelf } from '@/lib/discounts';
import { withMpSso } from '@/lib/payment';
import { addMedicalItem, removeMedicalItem, updateMedicalItem } from '@/lib/api';

type Step = 'who' | 'type' | 'details' | 'sign' | 'review' | 'done';

const STEP_NAME: Record<Step, string> = {
  who: 'Who',
  type: 'Registration',
  details: 'Questions',
  sign: 'Sign',
  review: 'Review',
  done: 'Done',
};

export interface Viewer {
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string | null;
}

interface Props {
  apiBaseUrl: string;
  /** The event, plus its art: the sheet wears the same band the page does. */
  event: ClientEvent & { image?: string | null };
  /**
   * How many dates one sign-up covers, or 0 when it covers only this one.
   *
   * `Events.Register_Into_Series` plus the future dates in the sequence. The
   * number is passed rather than derived because the sheet is never given the
   * series, and the last screen before paying is the last chance to say it.
   */
  seriesDates?: number;
  product: ClientProduct | null;
  form: ClientForm | null;
  viewer: Viewer | null;
  people: ClientPerson[];
  open: boolean;
  onClose: () => void;
  /** The page OLG returns to, bare. */
  pageUrl: string;
  /** A finished free registration, or a paid one whose redirect did not fire. */
  /**
   * `names` is who was just registered, where this is the end of a submit
   * rather than somebody returning to a finished one. The page uses it for
   * the moment it shows over the top; without it the moment still happens,
   * just without the names in it.
   */
  onRegistered: (invoiceGuid: string, claimedSuccess?: boolean, names?: string[]) => void;
  signIn: () => void;
  /** Link for a household member's unfinished checkout. */
  invoiceLink: (guid: string) => string;
  /** The registrations this event offers, when it has more than one. */
  doors?: GatheringDoor[];
  /** Volunteer shifts, for the registration that takes them. */
  shifts?: EventShift[];
  /** Each registration's own questions, by Form_ID. */
  doorForms?: Record<number, ClientForm>;
  /** What a campus question draws itself with. */
  campuses?: FormCampusOption[];
  /** What a medical question offers as chips: MP's own list. */
  medicalTypes?: MedicalType[];
  /** Contact ids already chosen on the page, so the sheet skips asking again. */
  preselect?: number[];
  /** People named on the page who are not in the household. */
  preselectGuests?: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    dateOfBirth?: string;
  }[];
  /**
   * Something true about a real MP record, learned while answering — who the
   * emergency contact is to the registrant, and sometimes their gender. It
   * goes to the record when it is tapped, not when the form is finished.
   */
  onLearn?: (fact: LearnedRelationship) => void;
  /** A gender a question had to ask for, on its way to their MP record now. */
  onLearnGender?: (contactId: number, gender: 'male' | 'female') => void;
  /** An experience's dress for the options and answers (lib/types SheetDress). */
  dress?: SheetDress;
  /** Tapping someone already registered: an experience opens their place. */
  onOpenRegistered?: (contactId: number) => void;
  /** What MP holds for the household's mailing address, for a form that asks. */
  householdAddress?: MailingAddress | null;
}

/** One line of a person's review, or the one card among them. */
type ReviewAnswer =
  | { kind: 'text'; label: string; value: string; under: number | null }
  | {
      kind: 'emergency';
      name: string;
      phone: string;
      relationship: string;
      image: string | null;
    };

let seq = 0;
const key = () => `r${++seq}-${Date.now()}`;

/**
 * Answers we already know. A prefilled value must be one the field offers;
 * if her stored size is not on this form's list, leave it blank so she
 * chooses, rather than quietly downgrading her.
 */
function prefilled(p: ClientPerson | undefined, form: ClientForm | null): Record<string, string> {
  const out: Record<string, string> = {};
  if (!p || !form) return out;
  for (const f of form.fields) {
    if (!f.prefill) continue;
    const v = p.defaults[f.prefill];
    if (!v) continue;
    const offered = f.optionsAllow?.length
      ? f.options.filter((o) => f.optionsAllow!.includes(o))
      : f.options;
    if (!offered.length || offered.some((o) => o.toLowerCase() === v.toLowerCase()))
      out[f.id] = offered.find((o) => o.toLowerCase() === v.toLowerCase()) ?? v;
  }
  return out;
}

function draftFor(p?: ClientPerson, form: ClientForm | null = null): RegistrantDraft {
  return {
    key: key(),
    contactId: p?.contactId ?? null,
    firstName: p?.firstName ?? '',
    lastName: p?.lastName ?? '',
    email: p?.email ?? '',
    phone: p?.phone ?? '',
    dateOfBirth: p?.dateOfBirth?.slice(0, 10) ?? '',
    options: {},
    // What the church can already answer, filled in: the stored answers a
    // field asks for by name, and the emergency contact on their record.
    // Most people arrive with one, so the question is a card to confirm.
    answers: { ...prefilled(p, form), ...seedAnswers(form, p) },
  };
}

const EMAIL = /.+@.+\..+/;

/**
 * The whole registration in one sheet: who is coming, what each person
 * picks and answers, then a review with the total. Nothing here talks to MP;
 * apps/api does, and it re-checks everything.
 *
 * Ported from the events microsite's RegistrationPanel (vaul) onto the
 * shared ResponsiveSheet so it portals into the widget's body-level overlay
 * host and clears the website's fixed header.
 */

export function RegistrationSheet({
  apiBaseUrl,
  event,
  seriesDates = 0,
  product,
  form,
  viewer,
  people,
  open,
  onClose,
  pageUrl,
  onRegistered,
  signIn,
  invoiceLink,
  doors = [],
  shifts = [],
  doorForms = {},
  campuses = [],
  medicalTypes = [],
  preselect = [],
  preselectGuests = [],
  onLearn,
  onLearnGender,
  dress,
  onOpenRegistered,
  householdAddress = null,
}: Props) {
  /**
   * ONE WAY IN IS NOT A QUESTION.
   *
   * With several registrations the sheet asks each person which is theirs.
   * With one there is nothing to choose, so everybody is given it: a serving
   * one opens straight on that person's shifts, and any other skips the step
   * altogether. Fall Family Fest's only registration is serving, and it
   * should read like Flannel Fest with the choosing taken out.
   */
  const onlyDoor = doors.length === 1 ? (doors[0] ?? null) : null;
  const hasTypes = doors.length > 1 || Boolean(onlyDoor?.takesShifts);
  // The page already asked who is coming, household and guests alike, so the
  // sheet opens on the first question it has not answered.
  const seeded = preselect.length + preselectGuests.length > 0;
  const seed = () => {
    if (!viewer) return [draftFor()];
    const chosen = preselect
      .map((id) => people.find((p) => p.contactId === id))
      .filter((p): p is ClientPerson => Boolean(p));
    return [
      ...chosen.map((p) => draftFor(p, form)),
      ...preselectGuests.map((g) => ({
        ...draftFor(undefined, form),
        firstName: g.firstName,
        lastName: g.lastName,
        email: g.email,
        phone: g.phone,
        dateOfBirth: g.dateOfBirth ?? '',
      })),
    ];
  };
  const [step, setStep] = useState<Step>(() => (seeded ? (hasTypes ? 'type' : 'details') : 'who'));
  const [registrants, setRegistrants] = useState<RegistrantDraft[]>(seed);
  const [active, setActive] = useState(0);
  /**
   * The registration step has two screens for a volunteer: the ways in, then
   * the shifts. Not a step of its own — the rail would gain and lose a stop
   * depending on what somebody tapped — and not a drill-in either, because it
   * is the same question continued, not a detour.
   */
  const [pickingShifts, setPickingShifts] = useState(false);
  /**
   * Forward or back, so a screen arrives from the side it came from. The flow
   * is a queue of people and steps, and a page that appears out of nowhere
   * gives nothing away about whether you just went on or stepped back.
   */
  const [dir, setDir] = useState<1 | -1>(1);
  const [purchaser, setPurchaser] = useState({
    firstName: viewer?.firstName ?? '',
    lastName: viewer?.lastName ?? '',
    email: viewer?.email ?? '',
    phone: viewer?.phone ?? '',
  });
  /** One per registration, for the household, when a form asks for it. */
  const [address, setAddress] = useState<MailingAddress>(householdAddress ?? EMPTY_ADDRESS);
  // The household can arrive after the sheet does; fill in only a blank one.
  useEffect(() => {
    if (householdAddress)
      setAddress((a) => (Object.values(a).every((v) => !v.trim()) ? householdAddress : a));
  }, [householdAddress]);
  const [promoInput, setPromoInput] = useState('');
  const [promo, setPromo] = useState<PromoApplied | null>(null);
  const [promoError, setPromoError] = useState<string | null>(null);
  const [automatic, setAutomatic] = useState<Record<string, PromoLine[]>>({});
  const [pricingNote, setPricingNote] = useState<string | null>(null);
  const [paymentOption, setPaymentOption] = useState<'full' | 'deposit'>('full');
  const [error, setError] = useState<string | null>(null);
  const [resumeGuid, setResumeGuid] = useState<string | null>(null);
  const [result, setResult] = useState<Extract<SubmitResult, { ok: true }> | null>(null);
  /**
   * The same result, readable from a callback handed to a hook.
   *
   * `onSettled` below is created once and fires minutes later, after the
   * payment window closes; closing over the state directly would hand it
   * whatever `result` was when that closure was made, which is null.
   */
  const resultRef = useRef(result);
  resultRef.current = result;
  const [pending, setPending] = useState(false);
  /** True from the moment we hand the page to OLG until it actually goes. */
  const leaving = useRef(false);
  // Money happens in a popup where a real window can open; the page waits.
  const pay = usePaymentWindow({
    apiBaseUrl,
    // Let "Payment received" be seen for a beat before the sheet gives way
    // to the invoice card on the page; a sheet that vanishes the instant the
    // window closes reads as a glitch.
    onSettled: (guid, claimed) => {
      window.setTimeout(
        () =>
          onRegistered(
            guid,
            claimed,
            resultRef.current?.participants.map((p) => p.name)
          ),
        1400
      );
    },
  });

  // Opened from the page with people already ticked: start there, on the
  // question the page has not asked.
  useEffect(() => {
    if (!open) return;
    if (!seeded) return;
    const party = seed();
    setRegistrants(party);
    setActive(0);
    setStep(hasTypes ? 'type' : 'details');
    // The cards show what each person will actually pay, so the discounts
    // have to be known before they are drawn. Coming in from the page skips
    // the who step, which is where that used to be asked for.
    if (hasTypes) void refreshPromosFor(party, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, preselect.join(','), preselectGuests.map((g) => g.firstName).join(','), hasTypes]);

  /**
   * REOPENING IS STARTING OVER, NOT RESUMING.
   *
   * `pickingShifts` is a screen position, not an answer, and it used to
   * survive the sheet closing. Tick Sarah, send her to Volunteer, close the
   * sheet, untick her and tick yourself: the effect above correctly rebuilt
   * the party as you, but the sheet was still standing on the shift list, so
   * it asked what YOU were helping with for a way in you never picked. Worse,
   * `typeOk` reads the shifts off a registration whose door is now undefined,
   * so Continue asked for a shift that could not be given and the way-in
   * question was unreachable. No way forward and no way back.
   *
   * The effect above rebuilds who is in the party; this rebuilds where the
   * sheet is standing, so the two cannot disagree. It runs for the dock's
   * unseeded path too, which the reseed above returns early from.
   */
  useEffect(() => {
    if (!open) return;
    setPickingShifts(false);
    setDir(1);
  }, [open]);

  // A sign-in mid-flow arrives as a new viewer. Start the party over from them.
  useEffect(() => {
    if (seeded) return;
    setRegistrants(viewer ? [] : [draftFor()]);
    setPurchaser({
      firstName: viewer?.firstName ?? '',
      lastName: viewer?.lastName ?? '',
      email: viewer?.email ?? '',
      phone: viewer?.phone ?? '',
    });
  }, [viewer?.firstName, viewer?.lastName, viewer?.email, viewer?.phone]); // eslint-disable-line react-hooks/exhaustive-deps

  const spotsLeft = event.capacity != null ? Math.max(0, event.capacity - event.registered) : null;
  const needsSignIn = event.forceLogin && !viewer;

  const pricing = useMemo(
    () =>
      priceRegistration(
        product
          ? {
              basePrice: product.basePrice,
              depositPrice: product.depositPrice,
              options: product.groups.flatMap((g) =>
                g.options.map((o) => ({ id: o.id, title: o.title, price: o.price, groupId: g.id }))
              ),
            }
          : null,
        registrants.map((r) => ({
          name: `${r.firstName} ${r.lastName}`.trim() || 'Guest',
          options: r.options,
          promos: automatic[r.key] ?? [],
        })),
        promo,
        paymentOption
      ),
    [product, registrants, promo, paymentOption, automatic]
  );

  /**
   * Option prices the registration step already chose. Their group IS the
   * registration, so asking it again as a product option would be the same
   * question twice with two answers possible.
   */
  const hiddenGroupIds = doors.map((d) => d.optionPriceId).filter(Boolean) as number[];

  /** The household member a draft is for, when it is one: their photo. */
  /**
   * MEDICAL ROWS CORRECTED WHILE THIS SHEET IS OPEN.
   *
   * `people` came from /me and is a prop, so a row edited here is written to
   * MP and then kept over the top of it, by contact id, until the page next
   * reloads. Everything downstream reads `personOf`, so the list, the MP
   * answer and the review screen all move together — there is no second place
   * holding a stale copy.
   */
  const [medicalEdits, setMedicalEdits] = useState<Record<number, MedicalItem[]>>({});

  const personOf = (r: RegistrantDraft | undefined): ClientPerson | null => {
    if (!r?.contactId) return null;
    const found = people.find((p) => p.contactId === r.contactId) ?? null;
    if (!found) return null;
    const mine = medicalEdits[found.contactId];
    return mine ? { ...found, medical: mine } : found;
  };

  /**
   * A row on file, corrected or taken off — written to MP's own
   * `Contact_Medical_Items`, not to this registration's answer, because the
   * record is what turns up again next year.
   *
   * Never optimistic. A medical row is the one thing on this form somebody
   * might read off a clipboard at the door, so it changes on screen only once
   * MP has said it changed.
   */
  const medicalFor = (contactId: number) =>
    medicalEdits[contactId] ?? people.find((p) => p.contactId === contactId)?.medical ?? [];

  const onSaveMedical = async (contactId: number, item: MedicalItem, patch: MedicalDraft) => {
    try {
      const { item: saved } = await updateMedicalItem(apiBaseUrl, event.id, contactId, item.id, {
        kind: patch.kind,
        typeId: patch.typeId,
        label: patch.label,
        severe: patch.severe,
        detail: patch.detail,
        expiresOn: patch.expiresOn,
        keptWhere: patch.keptWhere,
      });
      setMedicalEdits((m) => ({
        ...m,
        [contactId]: medicalFor(contactId).map((r) => (r.id === item.id ? saved : r)),
      }));
      return true;
    } catch {
      return false;
    }
  };

  /**
   * A row the family added during registration becomes a row on the record.
   *
   * Optimistic in the same way the corrections are: the list on screen gets
   * it straight away, and a failure takes it back off the list rather than
   * leaving a row that does not exist. The draft in the answer is untouched
   * either way, so nothing a parent typed is ever lost to a network error.
   */
  const onAddMedical = async (contactId: number, draft: MedicalDraft) => {
    try {
      const { item } = await addMedicalItem(apiBaseUrl, event.id, contactId, draft);
      setMedicalEdits((m) => ({ ...m, [contactId]: [...medicalFor(contactId), item] }));
      return true;
    } catch {
      return false;
    }
  };

  const onRemoveMedical = async (contactId: number, item: MedicalItem) => {
    try {
      await removeMedicalItem(apiBaseUrl, event.id, contactId, item.id);
      setMedicalEdits((m) => ({
        ...m,
        [contactId]: medicalFor(contactId).filter((r) => r.id !== item.id),
      }));
      return true;
    } catch {
      return false;
    }
  };

  /** The registration this person is coming as, and the form it asks. */
  /**
   * `r` is optional because the party is legitimately empty: `seed()` returns
   * [] for a signed-in viewer who has ticked nobody, and the sheet is
   * rendered on the page the whole time, closed, so every derived value runs
   * against an empty list on an ordinary page load.
   */
  // The only door counts as chosen from the first render. The effect below
  // writes it onto the drafts a render later, and without this fallback that
  // render showed the way-in screen, then swapped to the shifts, and the swap
  // played the screen transition over an empty body on a live page.
  const doorOf = (r: RegistrantDraft | undefined) => doorFor(doors, r) ?? (r ? onlyDoor : null);
  useEffect(() => {
    if (!onlyDoor) return;
    registrants.forEach((r, i) => {
      if (r.doorId !== onlyDoor.id) chooseDoor(i, onlyDoor);
    });
    // chooseDoor is recreated every render; the party and the door are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlyDoor?.id, registrants.map((r) => `${r.key}:${r.doorId ?? ''}`).join(',')]);
  const formFor = (r: RegistrantDraft) => {
    const door = doorOf(r);
    return (door?.formId ? doorForms[door.formId] : null) ?? form;
  };

  /**
   * Choosing a registration IS choosing its option price: that is the line
   * MP writes, so the price on the screen and the price on the invoice are
   * the same fact. Any previously chosen one steps aside, since the group is
   * mutually exclusive.
   */
  const chooseDoor = (index: number, door: GatheringDoor) => {
    setRegistrants((list) =>
      list.map((r, i) => {
        if (i !== index) return r;
        const others = { ...r.options };
        for (const d of doors) if (d.optionPriceId) delete others[d.optionPriceId];
        // Each registration asks its own form, so what the church already
        // knows has to be filled in against THAT form's field ids — the
        // campus question is a different field on each of the four, and
        // seeding only the event's form left three of them blank. Anything
        // already typed wins; this only fills what is empty.
        const theirForm = (door.formId ? doorForms[door.formId] : null) ?? form;
        const person = r.contactId
          ? (people.find((p) => p.contactId === r.contactId) ?? undefined)
          : undefined;
        const known = { ...prefilled(person, theirForm), ...seedAnswers(theirForm, person) };
        const answers = { ...r.answers };
        for (const [id, value] of Object.entries(known))
          if (!(answers[id] ?? '').trim()) answers[id] = value;
        return {
          ...r,
          doorId: door.id,
          shiftIds: door.takesShifts ? (r.shiftIds ?? []) : [],
          options: door.optionPriceId ? { ...others, [door.optionPriceId]: 1 } : others,
          answers,
        };
      })
    );
  };

  const toggleShift = (index: number, shiftId: number) => {
    setRegistrants((list) =>
      list.map((r, i) => {
        if (i !== index) return r;
        const have = r.shiftIds ?? [];
        return {
          ...r,
          shiftIds: have.includes(shiftId) ? have.filter((s) => s !== shiftId) : [...have, shiftId],
        };
      })
    );
  };

  /** A registration is chosen, and a volunteer has at least one shift. */
  const typeOk = (r: RegistrantDraft | undefined) => {
    if (!r) return false;
    if (!hasTypes) return true;
    const door = doorOf(r);
    if (!door) return false;
    return door.takesShifts ? (r.shiftIds ?? []).length > 0 : true;
  };

  /** Ask the server what applies today. Runs on entering review and after a price-changed refusal. */
  const refreshPromos = (code: string | null) => refreshPromosFor(registrants, code);

  async function refreshPromosFor(party: RegistrantDraft[], code: string | null) {
    try {
      const p = await previewPromos(
        apiBaseUrl,
        event.id,
        party.map((r) => ({ key: r.key, contactId: r.contactId })),
        code
      );
      setAutomatic(p.automatic);
      if (p.typed) {
        if (p.typed.ok) {
          setPromo({
            code: p.typed.promo.code,
            title: p.typed.promo.title,
            price: p.typed.promo.price,
          });
          setPromoError(null);
        } else {
          setPromo(null);
          setPromoError(p.typed.reason);
        }
      }
    } catch {
      /* preview only; submit decides */
    }
  }

  const update = (k: string, patch: Partial<RegistrantDraft>) =>
    setRegistrants((rs) => rs.map((r) => (r.key === k ? { ...r, ...patch } : r)));

  /**
   * One answer, merged into whatever is there NOW. A control that writes two
   * fields at once — the emergency contact writes a name and a number — used
   * to lose the first of them: both calls landed in the same tick, each
   * spreading the same stale copy of the answers, and the last one won.
   */
  const answer = (k: string, id: number, value: string) =>
    setRegistrants((rs) =>
      rs.map((r) => (r.key === k ? { ...r, answers: { ...r.answers, [id]: value } } : r))
    );

  const togglePerson = (p: ClientPerson) =>
    setRegistrants((rs) => {
      const has = rs.find((r) => r.contactId === p.contactId);
      if (has) return rs.filter((r) => r.key !== has.key);
      return [...rs, draftFor(p, form)];
    });

  const addGuest = () => setRegistrants((rs) => [...rs, draftFor()]);
  const removeGuest = (k: string) => setRegistrants((rs) => rs.filter((r) => r.key !== k));

  const whoOk =
    registrants.length > 0 &&
    registrants.every((r) => r.firstName.trim() && r.lastName.trim()) &&
    Boolean(viewer || (purchaser.email.trim() && EMAIL.test(purchaser.email)));

  const asks = (f: ClientForm | null) =>
    Boolean(f?.fields.some((x) => !x.hidden && x.kind !== 'instructions'));
  /**
   * The option groups that are still a question. The group whose options ARE
   * the registrations was answered by picking one.
   */
  const askedGroups = (product?.groups ?? []).filter(
    (g) => !hiddenGroupIds.some((id) => g.options.some((o) => o.id === id))
  );
  /**
   * WHO GETS ASKED, IN ORDER
   * ========================
   *
   * The questions used to be a board of people to tap into, one page each.
   * That put a choice in front of somebody where there was none to make —
   * everybody has to be answered for before the step is done, so the order
   * may as well be ours. It walks the party the way the registration step
   * does, and skips anybody their registration asks nothing of.
   */
  /**
   * Whose questions carry the mailing address: the first person whose form
   * has MP's "Get Address Info" on. It is the household's, so it is asked
   * once, with that person's questions.
   */
  const addressAt = registrants.findIndex((r) => Boolean(formFor(r)?.askAddress));
  const askOrder = registrants
    .map((r, i) => [r, i] as const)
    .filter(([r, i]) => askedGroups.length > 0 || asks(formFor(r)) || i === addressAt)
    .map(([, i]) => i);
  const hasDetails = askOrder.length > 0;

  /**
   * What this person still owes, as a count: a required option group with
   * nothing picked, or a required question that is visible and blank. The
   * board shows the number, the footer gates on it being zero, and both read
   * the form THIS person is being asked — their registration's, if it has
   * one — rather than the event's.
   */
  const owed = (r: RegistrantDraft | undefined): number => {
    if (!r) return 1;
    const theirForm = formFor(r);
    let left = 0;
    for (const g of product?.groups ?? []) {
      if (hiddenGroupIds.some((id) => g.options.some((o) => o.id === id))) continue;
      const picked = g.options.filter((o) => (r.options[o.id] ?? 0) > 0);
      if (g.required && !picked.length) left += 1;
    }
    // The waiver's tick and signature are asked once, at the end, for
    // everybody at once — so they are not this person's to owe here.
    const theirWaiver = waiverOf(theirForm);
    const atTheEnd = new Set(
      [theirWaiver?.agree?.id, theirWaiver?.signature?.id].filter(Boolean) as number[]
    );
    for (const f of theirForm?.fields ?? []) {
      if (f.kind === 'instructions' || f.kind === 'file' || f.parentId) continue;
      if (atTheEnd.has(f.id)) continue;
      if (
        f.required &&
        theirForm &&
        isVisible(theirForm, f, r.answers) &&
        !(r.answers[f.id] ?? '').trim()
      )
        left += 1;
    }
    return left;
  };

  const detailsOk = (r: RegistrantDraft | undefined) => owed(r) === 0;

  /**
   * WHY THE BUTTON IS GREY.
   *
   * Half the questions on this screen are drawn as cards rather than fields —
   * the emergency contact, the medical rows, the waiver — so "required" has
   * nowhere to put an asterisk, and a screen that looks finished with a dead
   * Continue under it is the page refusing and not saying so. This names the
   * first thing still owed, in the words the form used.
   */
  /**
   * Whether the shift list is what is on screen — derived, not remembered.
   * See lib/shift-screen: a flag on its own outlives the registration it was
   * about, and strands somebody on shifts for a way in they never picked.
   */
  const onShifts = showsShifts(
    pickingShifts || Boolean(onlyDoor?.takesShifts),
    doorOf(registrants[active]),
    shifts.length
  );

  const missing = (r: RegistrantDraft | undefined): string | null => {
    if (step === 'who') return whoOk ? null : 'Who is coming?';
    if (step === 'type') {
      if (!r) return null;
      if (onShifts) return typeOk(r) ? null : 'Pick at least one shift';
      return r.doorId ? null : 'Pick a registration';
    }
    if (step === 'sign') return allSigned ? null : 'Needs a signature';
    if (!r) return null;
    const theirForm = formFor(r);
    if (addressAt > -1 && registrants[addressAt]?.key === r.key) {
      const owed = addressMissing(address);
      if (owed) return owed;
    }
    for (const g of product?.groups ?? []) {
      if (hiddenGroupIds.some((id) => g.options.some((o) => o.id === id))) continue;
      if (g.required && !g.options.some((o) => (r.options[o.id] ?? 0) > 0)) return g.name;
    }
    const theirWaiver = waiverOf(theirForm);
    const atTheEnd = new Set(
      [theirWaiver?.agree?.id, theirWaiver?.signature?.id].filter(Boolean) as number[]
    );
    for (const f of theirForm?.fields ?? []) {
      if (f.kind === 'instructions' || f.kind === 'file' || f.parentId) continue;
      if (atTheEnd.has(f.id)) continue;
      if (
        f.required &&
        theirForm &&
        isVisible(theirForm, f, r.answers) &&
        !(r.answers[f.id] ?? '').trim()
      )
        return f.title || f.label;
    }
    return null;
  };

  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    setPromoError(null);
    setPending(true);
    try {
      const res = await checkPromo(
        apiBaseUrl,
        event.id,
        promoInput,
        registrants.map((r) => r.contactId).filter((id): id is number => Boolean(id))
      );
      if (res.ok) {
        setPromo({ code: res.code, title: res.title, price: res.price });
        setPromoInput('');
      } else {
        setPromo(null);
        setPromoError(res.error);
      }
    } catch (err) {
      setPromoError(err instanceof Error ? err.message : 'Could not check that code.');
    } finally {
      // Not while the browser is on its way to OLG: the spinner IS the
      // handoff, and clearing it would blink the button back to life a
      // moment before the page goes.
      if (!leaving.current) setPending(false);
    }
  };

  const send = async () => {
    setError(null);
    setPending(true);
    // Open the payment window on the click itself: a window opened after an
    // await is a window the browser blocks. It shows a placeholder until the
    // API has minted OLG's URL. Free registrations close it again unused.
    const win = pricing.total > 0 ? pay.begin() : null;
    try {
      const res = await submitRegistration(apiBaseUrl, {
        eventId: event.id,
        registrants,
        purchaser: viewer
          ? {
              ...purchaser,
              firstName: viewer.firstName,
              lastName: viewer.lastName,
              email: viewer.email || purchaser.email || '',
            }
          : purchaser,
        promoCode: promo?.code ?? null,
        paymentOption,
        expectedTotal: pricing.total,
        returnUrl: pageUrl,
        ...(addressAt > -1 ? { address } : {}),
      });
      if (res.ok) {
        setResult(res);
        // 'done' is set below for the paths that stay on this page. Leaving
        // for OLG keeps the spinner instead; see the payment branch.
        if (!(res.paymentUrl && !win)) setStep('done');
        // Money is owed: OLG's hosted card form, in the popup when we have
        // one, otherwise by leaving the page. The seats are already held.
        if (res.paymentUrl) {
          if (win) pay.launch(res.paymentUrl, res.invoiceGuid);
          else {
            /**
             * No popup — a phone, usually — so we leave the page ourselves,
             * carrying the session the way the window would have. See
             * withMpSso.
             *
             * AND WE STAY ON THE BUTTON'S SPINNER WHILE WE GO. Switching to
             * the held-spot card first put a screen up that nobody was meant
             * to read: it says "taking you to our secure payment page" and
             * then the page leaves underneath it. One motion, not a flash.
             *
             * The card is still the way out if the navigation does not
             * happen, which is what the timer is for. Four seconds is long
             * enough that a slow phone is not told something went wrong, and
             * short enough that a blocked navigation is not a dead end.
             */
            leaving.current = true;
            window.location.assign(withMpSso(res.paymentUrl));
            window.setTimeout(() => {
              if (!leaving.current) return;
              leaving.current = false;
              setPending(false);
              setStep('done');
            }, 4000);
          }
        } else {
          pay.abort();
          onRegistered(
            res.invoiceGuid,
            false,
            res.participants.map((p) => p.name)
          );
        }
      } else {
        pay.abort();
        setError(res.error);
        setResumeGuid(res.resumeInvoiceGuid ?? null);
        if (res.priceChanged) {
          setPricingNote("Updated to today's prices.");
          await refreshPromos(promo?.code ?? null);
        }
      }
    } catch (err) {
      pay.abort();
      setError(errorText(err, 'Something went wrong. Nothing was charged.'));
    } finally {
      setPending(false);
    }
  };

  const toReview = () => {
    setStep('review');
    void refreshPromos(promo?.code ?? null);
  };
  /** The documents, then the money. */
  const afterDetails = () => (hasSign ? setStep('sign') : toReview());
  const toDetails = () => {
    const first = askOrder[0];
    if (first === undefined) return afterDetails();
    setActive(first);
    setStep('details');
  };
  /** Off this person's registration: the next one's, or on to the questions. */
  const afterType = () => {
    setPickingShifts(false);
    if (active < registrants.length - 1) setActive(active + 1);
    else toDetails();
  };

  /**
   * A tap on a registration is the answer to the only question on the screen,
   * so it carries itself forward — after a beat, so the tick is seen landing
   * rather than the screen appearing to change under the finger. Picking the
   * volunteer registration goes to its shifts instead, since a volunteer
   * without one is not registered for anything.
   */
  const advance = useRef<number | null>(null);
  useEffect(() => () => void (advance.current && window.clearTimeout(advance.current)), []);
  const pickDoor = (door: GatheringDoor) => {
    setError(null);
    setDir(1);
    chooseDoor(active, door);
    if (advance.current) window.clearTimeout(advance.current);
    advance.current = window.setTimeout(() => {
      if (door.takesShifts && shifts.length > 0) setPickingShifts(true);
      else afterType();
      // Long enough to watch the tick land and read as an answer, short
      // enough that nobody reaches for the button.
    }, 420);
  };

  const goNext = () => {
    setError(null);
    setDir(1);
    if (advance.current) window.clearTimeout(advance.current);
    if (step === 'who') {
      setActive(0);
      if (hasTypes) {
        setStep('type');
        // The discounts are shown ON the cards, so they have to be known
        // before the cards are drawn rather than at review.
        void refreshPromos(promo?.code ?? null);
      } else toDetails();
    } else if (step === 'type') {
      const door = registrants[active] ? doorOf(registrants[active]) : null;
      if (!onShifts && door?.takesShifts && shifts.length > 0) setPickingShifts(true);
      else afterType();
    } else if (step === 'details') {
      const at = askOrder.indexOf(active);
      const next = at > -1 ? askOrder[at + 1] : undefined;
      if (next !== undefined) setActive(next);
      else afterDetails();
    } else if (step === 'sign') {
      toReview();
    }
  };
  const goBack = () => {
    setError(null);
    setDir(-1);
    if (advance.current) window.clearTimeout(advance.current);
    const toTypes = () => {
      setActive(registrants.length - 1);
      setPickingShifts(false);
      setStep('type');
    };
    const toLastQuestion = () => {
      const last = askOrder[askOrder.length - 1];
      if (last !== undefined) {
        setActive(last);
        setStep('details');
      } else if (hasTypes) toTypes();
      else setStep('who');
    };
    if (step === 'review') {
      if (hasSign) setStep('sign');
      else toLastQuestion();
    } else if (step === 'sign') {
      toLastQuestion();
    } else if (step === 'details') {
      const at = askOrder.indexOf(active);
      const prev = at > 0 ? askOrder[at - 1] : undefined;
      if (prev !== undefined) setActive(prev);
      else if (hasTypes) toTypes();
      else setStep('who');
    } else if (step === 'type') {
      // Leaving the shifts IS going back to the way in. The flag comes off
      // either way, so a stale one cannot swallow the press and leave the
      // button looking broken.
      const leavingShifts = onShifts && !onlyDoor;
      setPickingShifts(false);
      if (leavingShifts) return;
      if (active > 0) setActive(active - 1);
      else setStep('who');
    }
  };

  /**
   * Whose screen comes next, when it is somebody else's: the button says the
   * name so a household of four knows how far through it is. Null while the
   * next screen is still this person's — a volunteer's shifts.
   */
  /**
   * THE DOCUMENTS, GATHERED
   * =======================
   *
   * A waiver belongs to the registration, not to a person, so every
   * registrant asked the same document is folded into one card signed once.
   * Two ways in can share a form (or carry the same waiver on different
   * forms); grouping by the field's own id is what makes a household of three
   * one signature instead of three.
   */
  const waivers: WaiverGroup[] = (() => {
    const groups = new Map<number, WaiverGroup>();
    for (const r of registrants) {
      const fields = waiverOf(formFor(r));
      if (!fields) continue;
      const person = personOf(r);
      const covers = {
        key: r.key,
        firstName: r.firstName || 'Guest',
        image: person?.image ?? null,
      };
      const had = groups.get(fields.doc.id);
      if (had) {
        had.covers.push(covers);
        continue;
      }
      const agreed = fields.agree ? Boolean((r.answers[fields.agree.id] ?? '').trim()) : true;
      const signature = fields.signature ? (r.answers[fields.signature.id] ?? '') : '';
      groups.set(fields.doc.id, {
        fields,
        covers: [covers],
        agreed,
        signature,
        // A name, not a word count: the box writes whatever the person's
        // record says, and a household whose name is one word was stuck on a
        // dead button with no way to see why.
        //
        // And only where the document asks for one. A waiver that is a single
        // checkbox has nowhere to write a name, so requiring one left the
        // step permanently unfinishable — the tick IS the agreement there.
        signed: agreed && (!fields.signature || signature.trim().length > 1),
      });
    }
    return [...groups.values()];
  })();
  /**
   * Whose name goes on the line. The person doing the registering signs, not
   * the child being registered: on this form it is a parent agreeing on their
   * behalf. Signed in we know it; a guest has just typed it; and if somehow
   * neither, the first person in the party is the only name we have.
   */
  const signerName =
    (viewer
      ? `${viewer.firstName} ${viewer.lastName}`
      : `${purchaser.firstName} ${purchaser.lastName}`
    ).trim() || `${registrants[0]?.firstName ?? ''} ${registrants[0]?.lastName ?? ''}`.trim();

  /**
   * Who is registered outright rather than holding a seat: a door with no
   * price on it and no discount doing the work. The API writes these on their
   * own settled invoice, so the confirmation must not tell somebody their
   * volunteer shift is waiting on a card.
   */
  const freeNames = registrants
    .filter(
      (r, i) =>
        (pricing.registrants[i]?.subtotal ?? 0) === 0 && !(automatic[r.key] ?? []).length && !promo
    )
    .map((r) => r.firstName || 'Guest');

  /**
   * What one person actually answered, labelled the way they were asked, with
   * the document's own fields left out: a review that reprinted the waiver's
   * tick and the signature would be showing somebody the fine print twice.
   */
  const answersOf = (r: RegistrantDraft): ReviewAnswer[] => {
    const theirForm = formFor(r);
    if (!theirForm) return [];
    const doc = waiverOf(theirForm);
    const who = emergencyFieldsOf(theirForm);
    // The document's own fields are the fine print, shown once at the end;
    // the emergency contact's three fields are one person, drawn as one.
    const skip = new Set(
      [
        doc?.doc.id,
        doc?.agree?.id,
        doc?.signature?.id,
        who?.name.id,
        who?.phone?.id,
        who?.relationship?.id,
      ].filter(Boolean) as number[]
    );

    const out: ReviewAnswer[] = [];
    for (const f of theirForm.fields) {
      if (f.hidden || f.kind === 'instructions' || skip.has(f.id)) continue;
      const value = (r.answers[f.id] ?? '').trim();
      if (!value) continue;
      out.push({
        kind: 'text',
        label: f.title || f.label,
        value,
        // What this question hangs off, when it only exists because of an
        // earlier answer. MP's own dependency first; a `visibleWhen` rule of
        // ours says the same thing in more words.
        under: f.dependsOn ?? f.visibleWhen?.all?.[0]?.field ?? null,
      });
    }

    const name = who ? (r.answers[who.name.id] ?? '').trim() : '';
    if (name) {
      const match = people.find((p) => `${p.firstName} ${p.lastName}`.trim() === name);
      out.push({
        kind: 'emergency',
        name,
        phone: who?.phone ? (r.answers[who.phone.id] ?? '').trim() : '',
        relationship: who?.relationship ? (r.answers[who.relationship.id] ?? '').trim() : '',
        image: match?.image ?? personOf(r)?.emergency?.image ?? null,
      });
    }
    return out;
  };

  const hasSign = waivers.length > 0;
  const allSigned = waivers.every((w) => w.signed);

  /** A tick or a signature lands on everyone the document covers. */
  const signAll = (group: WaiverGroup, field: 'agree' | 'signature', value: string) => {
    const target = group.fields[field];
    if (!target) return;
    setRegistrants((rs) =>
      rs.map((r) =>
        group.covers.some((c) => c.key === r.key)
          ? { ...r, answers: { ...r.answers, [target.id]: value } }
          : r
      )
    );
  };

  /**
   * Whether this screen is finished. One expression, read by the footer's
   * button and by the Enter key, so the two can never disagree about whether
   * there is anything left to answer.
   */
  const canAdvance =
    step === 'who'
      ? whoOk
      : step === 'type'
        ? onShifts
          ? typeOk(registrants[active])
          : Boolean(registrants[active]?.doorId)
        : step === 'sign'
          ? allSigned
          : detailsOk(registrants[active]);

  /**
   * ENTER MOVES ON, the way it does in every other form somebody has filled
   * in. Typing a phone number and reaching for the mouse to press Continue is
   * a small tax paid on every screen of this, times everybody in the party.
   *
   * What it must not do: swallow a newline in the note box, or fire twice
   * when the thing already focused is a button — Enter is that button's own
   * activation, and the browser has already done it. `composedPath()[0]` and
   * not `target`, because the sheet is inside a shadow root and `target` is
   * retargeted to the host.
   */
  useEffect(() => {
    if (!open || needsSignIn || step === 'done') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || e.isComposing) return;
      if (e.shiftKey || e.metaKey || e.ctrlKey || e.altKey) return;
      const el = (e.composedPath()[0] ?? e.target) as HTMLElement | null;
      const tag = el?.tagName;
      if (!tag) return;
      if (tag === 'TEXTAREA' || tag === 'A' || tag === 'SUMMARY') return;
      /**
       * A button is the interesting case. Somebody who TABBED to a shift and
       * pressed Enter means "tick this" — that is what Enter does to a focused
       * control and the browser is about to do it. Somebody who CLICKED the
       * shift a moment ago also still has it focused, and Enter there means
       * "go on", not "untick the thing I just ticked" — which is what used to
       * happen. `:focus-visible` is exactly that difference: the browser sets
       * it for keyboard focus and not for a mouse click.
       */
      if (tag === 'BUTTON') {
        let keyboard = true;
        try {
          keyboard = el.matches(':focus-visible');
        } catch {
          /* no :focus-visible here: leave the button alone */
        }
        if (keyboard) return;
      }
      if (el?.isContentEditable) return;
      // A field that has its own idea of what Enter means. The promo box is
      // the one that matters: Enter there applies a code, and sending
      // somebody to a payment window instead would be unforgivable.
      if (el?.dataset?.enter) return;
      if (pending) return;
      if (step === 'review') {
        e.preventDefault();
        void send();
        return;
      }
      if (!canAdvance) return;
      e.preventDefault();
      goNext();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  /** Which screen is showing, and which way it arrived. */
  const screen = needsSignIn ? 'sign-in' : `${step}:${active}:${onShifts}`;

  const upNext = (() => {
    if (step === 'type') {
      const door = registrants[active] ? doorOf(registrants[active]) : null;
      if (!onShifts && door?.takesShifts && shifts.length > 0) return null;
      return registrants[active + 1]?.firstName || null;
    }
    if (step === 'details') {
      const at = askOrder.indexOf(active);
      const next = at > -1 ? askOrder[at + 1] : undefined;
      return next !== undefined ? registrants[next]?.firstName || null : null;
    }
    return null;
  })();

  const steps = (
    [
      ...(seeded ? [] : ['who']),
      ...(hasTypes ? ['type'] : []),
      ...(hasDetails ? ['details'] : []),
      ...(hasSign ? ['sign'] : []),
      'review',
    ] as Step[]
  ).filter(() => step !== 'done');

  return (
    <ResponsiveSheet
      open={open}
      onClose={onClose}
      maxWidth="max-w-3xl"
      panelClassName="bg-ground text-ink md:rounded-3xl"
      noPanelPadding
      hideCloseButton
      header={
        /* The event's own band, the way the page wears it. Shared with the
           questions sheet so both feel like the same night. */
        <EventSheetBand
          image={event.image}
          title={event.title}
          start={event.start}
          end={event.end}
          onClose={onClose}
        >
          {steps.length > 0 && (
            /* A rail, not a breadcrumb: how far in you are is worth a glance,
               and the steps are not links anyway. */
            <ol className="relative flex gap-1.5 px-5 pb-4">
              {steps.map((s) => {
                const at = steps.indexOf(step);
                const mine = steps.indexOf(s);
                return (
                  <li key={s} className="min-w-0 flex-1">
                    <div
                      className={`h-1 rounded-full transition ${mine <= at ? 'bg-teal' : 'bg-white/25'}`}
                    />
                    <div
                      className={`kicker mt-1.5 truncate text-[0.8rem]! ${
                        s === step ? 'text-white!' : 'text-white/50!'
                      }`}
                    >
                      {s === 'type' && onlyDoor ? onlyDoor.label : STEP_NAME[s]}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </EventSheetBand>
      }
      footer={
        !needsSignIn && step !== 'done' ? (
          <div className="ed glass flex items-center gap-2 border-x-0 border-b-0 px-5 py-4">
            {/* Nothing to go back TO on the first step: when the page asked who
                is coming, the who step is not in this sheet. */}
            {offersBack({
              // With one way in there is no way-in screen behind the shifts.
              onShifts: onShifts && !onlyDoor,
              atWhoStep: step === 'who',
              atFirstStep: steps.indexOf(step) === 0,
              atFirstPerson: active === 0,
              seeded,
            }) && (
              <button
                type="button"
                className="btn btn-quiet shrink-0"
                onClick={goBack}
                disabled={pending}
              >
                Back
              </button>
            )}
            {/*
             * `min-w-0` IS WHAT KEEPS THE BUTTON ON THE SCREEN.
             *
             * A flex item will not shrink below its own min-content width
             * unless it is told it may, so this group sat at the width of the
             * hint plus the button and pushed itself off the right edge of a
             * phone: measured at 519px inside a 437px window, with Continue
             * hanging past the screen. The hint is the one thing here that can
             * give — it already truncates — so it gives, and everything with a
             * fixed size says so.
             */}
            <div className="ml-auto flex min-w-0 items-center gap-3">
              {pricing.total > 0 && (
                <span className="text-navy shrink-0 text-sm font-bold">
                  {fmtMoney(pricing.amountDue)}
                  {pricing.amountDue !== pricing.total && (
                    <span className="text-slate text-xs font-semibold">
                      {' '}
                      of {fmtMoney(pricing.total)}
                    </span>
                  )}
                </span>
              )}
              {/* Said quietly, beside the button it explains. */}
              {!canAdvance && step !== 'review' && missing(registrants[active]) && (
                <span className="text-slate max-w-[14rem] min-w-0 truncate text-xs font-semibold">
                  {missing(registrants[active])}
                </span>
              )}
              {step === 'review' ? (
                <button
                  type="button"
                  className="btn btn-primary shrink-0"
                  onClick={send}
                  disabled={pending}
                >
                  {pending
                    ? 'One moment…'
                    : pricing.amountDue > 0
                      ? 'Continue to payment'
                      : onlyDoor?.takesShifts && !product
                        ? 'Sign up to serve'
                        : 'Register'}
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary shrink-0"
                  onClick={goNext}
                  disabled={!canAdvance}
                >
                  {upNext ? `Next: ${upNext}` : 'Continue'}
                  <ChevronRight className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        ) : null
      }
    >
      <SheetPage name="main">
        <SheetBody scrollKey={screen}>
          <Slide id={screen} dir={dir}>
            {needsSignIn ? (
              <div className="card p-6 text-center">
                <UserRound className="text-slate mx-auto h-8 w-8" />
                <h3 className="text-navy mt-3 text-lg font-extrabold">Sign in to register</h3>
                <p className="text-slate mt-1 text-sm">
                  This event asks everyone to register with their Woodside account so we know who is
                  coming.
                </p>
                <button type="button" className="btn btn-navy mt-4 w-full" onClick={signIn}>
                  Sign in with Woodside
                </button>
              </div>
            ) : step === 'who' ? (
              <WhoStep
                viewer={viewer}
                people={people}
                registrants={registrants}
                togglePerson={togglePerson}
                addGuest={addGuest}
                removeGuest={removeGuest}
                update={update}
                purchaser={purchaser}
                setPurchaser={setPurchaser}
                prohibitGuests={event.prohibitGuests}
                onOpenRegistered={onOpenRegistered}
                minor={event.minorRegistration}
                spotsLeft={spotsLeft}
                signIn={signIn}
                invoiceLink={invoiceLink}
              />
            ) : step === 'type' ? (
              onShifts ? (
                <ShiftPicker
                  person={{
                    firstName: registrants[active]?.firstName || 'this guest',
                    lastName: registrants[active]?.lastName,
                    image: personOf(registrants[active])?.image ?? null,
                  }}
                  shifts={shifts}
                  chosen={registrants[active]?.shiftIds ?? []}
                  onToggle={(id) => toggleShift(active, id)}
                />
              ) : (
                <RegistrationTypes
                  person={{
                    firstName: registrants[active]?.firstName || 'this guest',
                    lastName: registrants[active]?.lastName,
                    image: personOf(registrants[active])?.image ?? null,
                    cppCurrent: personOf(registrants[active])?.cppCurrent ?? false,
                    registrations: personOf(registrants[active])?.registrations,
                  }}
                  index={active}
                  count={registrants.length}
                  doors={doors}
                  shifts={shifts}
                  chosenDoorId={registrants[active]?.doorId ?? null}
                  onChoose={pickDoor}
                  /*
                   * What the page already said they would pay, until the
                   * preview comes back with today's answer. /me priced them
                   * before this sheet opened, so the card opens showing the
                   * discount rather than showing the sticker price and
                   * correcting itself a moment later.
                   */
                  promos={
                    automatic[registrants[active]?.key ?? ''] ??
                    personOf(registrants[active])?.promos ??
                    []
                  }
                />
              )
            ) : step === 'details' ? (
              <DetailsStep
                registrant={registrants[active]}
                image={personOf(registrants[active])?.image ?? null}
                person={personOf(registrants[active])}
                household={people}
                medicalTypes={medicalTypes}
                subtitle={registrants[active] ? (doorOf(registrants[active])?.label ?? null) : null}
                index={Math.max(0, askOrder.indexOf(active))}
                count={askOrder.length}
                product={product}
                form={registrants[active] ? formFor(registrants[active]) : form}
                campuses={campuses}
                update={update}
                answer={answer}
                onLearn={onLearn}
                onLearnGender={onLearnGender}
                onSaveMedical={onSaveMedical}
                onRemoveMedical={onRemoveMedical}
                onAddMedical={onAddMedical}
                hideGroupIds={hiddenGroupIds}
                dress={dress}
                address={
                  addressAt > -1 && active === addressAt
                    ? { value: address, onChange: setAddress }
                    : undefined
                }
              />
            ) : step === 'sign' ? (
              <SignStep
                waivers={waivers}
                signer={signerName}
                onAgree={(g, yes) =>
                  signAll(g, 'agree', yes ? (g.fields.agree?.options[0] ?? 'Yes') : '')
                }
                onSign={(g, name) => signAll(g, 'signature', name)}
              />
            ) : step === 'review' ? (
              <ReviewStep
                registrants={registrants}
                shiftsFor={(r) => {
                  const shorten = trimShared(shifts.map((s) => s.title));
                  return (r.shiftIds ?? [])
                    .map((id) => shifts.find((s) => s.id === id))
                    .filter((s): s is EventShift => Boolean(s))
                    .map((s) => shorten(s.title).trim());
                }}
                seriesDates={seriesDates}
                answersFor={answersOf}
                photoOf={(r) => personOf(r)?.image ?? null}
                pricing={pricing}
                product={product}
                promo={promo}
                promoInput={promoInput}
                setPromoInput={setPromoInput}
                applyPromo={applyPromo}
                clearPromo={() => setPromo(null)}
                promoError={promoError}
                paymentOption={paymentOption}
                setPaymentOption={setPaymentOption}
                pending={pending}
                automatic={automatic}
                pricingNote={pricingNote}
              />
            ) : (
              result && (
                <DoneStep
                  result={result}
                  faces={registrants.map((r) => ({
                    name: `${r.firstName} ${r.lastName}`.trim() || 'Guest',
                    image: personOf(r)?.image ?? null,
                  }))}
                  freeNames={freeNames}
                  heldNames={registrants
                    .filter((r) => !freeNames.includes(r.firstName || 'Guest'))
                    .map((r) => r.firstName || 'Guest')}
                  eventTitle={event.title}
                  invoiceLink={invoiceLink}
                  onView={() => onRegistered(result.invoiceGuid)}
                  pay={pay}
                  onPayHere={() =>
                    result.paymentUrl && window.location.assign(withMpSso(result.paymentUrl))
                  }
                  onOpenAgain={() => {
                    // A fresh token for the same invoice, in a fresh window.
                    const w = pay.begin();
                    payInvoice(apiBaseUrl, result.invoiceGuid, pageUrl, result.amountDue)
                      .then(({ url }) =>
                        w ? pay.launch(url, result.invoiceGuid) : window.location.assign(url)
                      )
                      .catch(() => {
                        pay.abort();
                        setError('Could not restart the payment. Use the pay-later link below.');
                      });
                  }}
                />
              )
            )}
            {error && (
              <div className="bg-rose-soft text-rose mt-4 rounded-2xl px-4 py-3 text-sm font-semibold">
                {error}
                {resumeGuid && (
                  <a href={invoiceLink(resumeGuid)} className="text-navy mt-2 block underline">
                    Finish paying for that registration
                  </a>
                )}
              </div>
            )}
          </Slide>
        </SheetBody>
      </SheetPage>
    </ResponsiveSheet>
  );
}

/**
 * ONE SCREEN GIVING WAY TO THE NEXT
 * =================================
 *
 * The flow is a queue — people, and steps within each person — and every one
 * of them is a card list in the same frame. Without a direction, going on and
 * stepping back look identical, which is how somebody loses their place after
 * the third "Next: Sarah". So a screen enters from the side the flow is
 * moving and the one it replaces leaves the other way.
 *
 * `popLayout` takes the outgoing screen out of the flow while it goes, so the
 * panel's own height tween follows the INCOMING screen rather than the sum of
 * the two. And the horizontal travel is clipped rather than hidden:
 * `overflow-x: clip` stops the slide widening the sheet without making a
 * scroll container of it, which `hidden` would — that would trap the campus
 * dropdown inside the box.
 */
const SLIDE = {
  enter: (d: number) => ({ opacity: 0, x: d * 26 }),
  here: { opacity: 1, x: 0 },
  leave: (d: number) => ({ opacity: 0, x: d * -26 }),
};

function Slide({ id, dir, children }: { id: string; dir: 1 | -1; children: React.ReactNode }) {
  return (
    /* The clip keeps a sliding step from widening the sheet, and it used to
       cut the cards inside it: a hovered card lifts and grows, and its shadow
       is wider than the card. The box is padded and pulled back out again, so
       what is clipped is the travel and not the furniture. */
    <div className="-mx-3 px-3" style={{ overflowX: 'clip' }}>
      <AnimatePresence mode="popLayout" initial={false} custom={dir}>
        <motion.div
          key={id}
          custom={dir}
          variants={SLIDE}
          initial="enter"
          animate="here"
          exit="leave"
          transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
        >
          {children}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

/**
 * Scroll area padding that leaves room for the pinned footer on a phone, and
 * the top of the next step. A form long enough to scroll (this event's waiver
 * is a page of it) otherwise hands the next step over already scrolled past
 * its own content, which reads as an empty sheet.
 */
function SheetBody({ scrollKey, children }: { scrollKey: string; children: React.ReactNode }) {
  const { mode } = useResponsiveSheet();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    for (let el = ref.current?.parentElement; el; el = el.parentElement) {
      const overflows = /auto|scroll/.test(getComputedStyle(el).overflowY);
      if (overflows && el.scrollHeight > el.clientHeight + 1) {
        el.scrollTop = 0;
        return;
      }
    }
  }, [scrollKey]);
  return (
    // On a desktop the sheet takes the room it is given: a floor under the
    // content so the panel stops resizing around every step, and one step of
    // three cards does not open a modal the size of a dialog box.
    <div ref={ref} className={`ed px-5 pt-2 ${mode === 'sheet' ? 'pb-8' : 'min-h-[24rem] pb-6'}`}>
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ */

/**
 * One person the viewer might register.
 *
 * Three states, and the third is the point: a PENDING tie is a relationship
 * somebody claimed and the other family has not answered yet. It is shown, so
 * the viewer can see they already asked and does not ask twice, and it cannot
 * be selected, because a claim on a record that is not yours must buy nothing
 * until a human agrees. Nothing here discloses anything the viewer did not
 * already supply.
 */
function PersonChoice({
  person: p,
  on,
  toggle,
  invoiceLink,
  onOpenRegistered,
}: {
  person: ClientPerson;
  on: boolean;
  toggle: (p: ClientPerson) => void;
  invoiceLink: (guid: string) => string;
  onOpenRegistered?: (contactId: number) => void;
}) {
  const name = `${p.firstName} ${p.lastName}`.trim();

  if (p.already === 'registered' && onOpenRegistered) {
    // An experience can show her place: tapping her opens it.
    return (
      <button
        type="button"
        onClick={() => onOpenRegistered(p.contactId)}
        className="card lift flex w-full items-center gap-3 p-3 text-left"
      >
        <Avatar name={name} image={p.image} size={38} />
        <span className="min-w-0 flex-1">
          <span className="text-navy block truncate text-sm font-bold">{p.firstName}</span>
          <span className="text-teal-deep block text-[0.7rem] font-semibold">
            Already registered · See their place
          </span>
        </span>
        <ChevronRight className="text-slate h-4 w-4 shrink-0" />
      </button>
    );
  }

  if (p.already) {
    return (
      <div className="card flex items-center gap-3 p-3 opacity-80">
        <Avatar name={name} image={p.image} size={38} />
        <span className="min-w-0 flex-1">
          <span className="text-navy block truncate text-sm font-bold">{p.firstName}</span>
          {p.already === 'registered' ? (
            <span className="text-teal-deep block text-[0.7rem] font-semibold">
              Already registered
            </span>
          ) : (
            <a
              href={p.invoiceGuid ? invoiceLink(p.invoiceGuid) : '#'}
              className="text-amber block text-[0.7rem] font-semibold underline"
            >
              Checkout unfinished. Pay now
            </a>
          )}
        </span>
        <span className="bg-teal flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-white">
          <Check className="h-3.5 w-3.5" />
        </span>
      </div>
    );
  }

  if (p.tie === 'pending') {
    return (
      <div className="card flex items-center gap-3 p-3 opacity-70">
        <Avatar name={name} image={p.image} size={38} />
        <span className="min-w-0 flex-1">
          <span className="text-navy block truncate text-sm font-bold">{name}</span>
          <span className="text-slate block truncate text-[0.7rem]">
            {p.relationship ? `${p.relationship} · ` : ''}waiting on their family
          </span>
        </span>
        <span className="bg-mist text-slate flex h-6 w-6 shrink-0 items-center justify-center rounded-full">
          <Clock className="h-3.5 w-3.5" />
        </span>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => toggle(p)}
      className={`card lift flex items-center gap-3 p-3 text-left ${on ? '!ring-teal !ring-2' : ''}`}
      aria-pressed={on}
    >
      <Avatar name={name} image={p.image} size={38} />
      <span className="min-w-0 flex-1">
        <span className="text-navy block truncate text-sm font-bold">{name}</span>
        <span className="text-slate block truncate text-[0.7rem]">
          {p.isViewer ? 'You' : (p.relationship ?? p.position ?? '')}
        </span>
      </span>
      <span
        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${on ? 'bg-teal text-white' : 'bg-mist'}`}
      >
        {on && <Check className="h-3.5 w-3.5" />}
      </span>
    </button>
  );
}

function WhoStep({
  viewer,
  people,
  registrants,
  togglePerson,
  addGuest,
  removeGuest,
  update,
  purchaser,
  setPurchaser,
  prohibitGuests,
  minor,
  spotsLeft,
  signIn,
  invoiceLink,
  onOpenRegistered,
}: {
  viewer: Viewer | null;
  people: ClientPerson[];
  registrants: RegistrantDraft[];
  togglePerson: (p: ClientPerson) => void;
  addGuest: () => void;
  removeGuest: (k: string) => void;
  update: (k: string, patch: Partial<RegistrantDraft>) => void;
  purchaser: { firstName: string; lastName: string; email: string; phone: string };
  setPurchaser: (p: { firstName: string; lastName: string; email: string; phone: string }) => void;
  prohibitGuests: boolean;
  minor: boolean;
  spotsLeft: number | null;
  signIn: () => void;
  invoiceLink: (guid: string) => string;
  onOpenRegistered?: (contactId: number) => void;
}) {
  const guests = registrants.filter((r) => !r.contactId);
  return (
    <div className="space-y-5">
      {spotsLeft != null && spotsLeft <= 20 && (
        <p
          className={`rounded-2xl px-4 py-2.5 text-sm font-semibold ${spotsLeft === 0 ? 'bg-rose-soft text-rose' : 'bg-amber-soft text-amber'}`}
        >
          {spotsLeft === 0
            ? 'This event is full.'
            : `${spotsLeft} ${spotsLeft === 1 ? 'spot' : 'spots'} left`}
        </p>
      )}

      {viewer && people.length > 0 && (
        <>
          <section>
            <h3 className="kicker mb-2">Who&apos;s coming?</h3>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {people
                .filter((p) => p.tie === 'household')
                .map((p) => (
                  <PersonChoice
                    key={p.contactId}
                    person={p}
                    on={registrants.some((r) => r.contactId === p.contactId)}
                    toggle={togglePerson}
                    invoiceLink={invoiceLink}
                    onOpenRegistered={onOpenRegistered}
                  />
                ))}
            </div>
          </section>

          {people.some((p) => p.tie !== 'household') && (
            <section>
              {/*
               * Not "other people": a stepson at his mother's has been in MP
               * as somebody's son for a decade, and it is our own data model
               * that kept losing him.
               *
               * And not "family" either, which is what this said first. MP
               * allows sixteen relationship types to register online and
               * three of them are nobody's family — Adult Cared for by,
               * Foreign Exchange Student of, Legal Ward of. Relationship is
               * the word that is true of all sixteen.
               */}
              <h3 className="kicker mb-2">Other relationships</h3>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {people
                  .filter((p) => p.tie !== 'household')
                  .map((p) => (
                    <PersonChoice
                      key={p.contactId}
                      person={p}
                      on={registrants.some((r) => r.contactId === p.contactId)}
                      toggle={togglePerson}
                      invoiceLink={invoiceLink}
                      onOpenRegistered={onOpenRegistered}
                    />
                  ))}
              </div>
            </section>
          )}
        </>
      )}

      {!viewer && (
        <section className="space-y-3">
          <h3 className="kicker">Your details</h3>
          <div className="grid grid-cols-2 gap-2">
            <input
              className="field"
              placeholder="First name"
              value={registrants[0]?.firstName ?? ''}
              onChange={(e) => update(registrants[0]?.key ?? '', { firstName: e.target.value })}
              autoComplete="given-name"
            />
            <input
              className="field"
              placeholder="Last name"
              value={registrants[0]?.lastName ?? ''}
              onChange={(e) => update(registrants[0]?.key ?? '', { lastName: e.target.value })}
              autoComplete="family-name"
            />
          </div>
          <input
            className="field"
            type="email"
            placeholder="Email"
            value={purchaser.email}
            onChange={(e) =>
              setPurchaser({
                ...purchaser,
                email: e.target.value,
                firstName: registrants[0]?.firstName ?? '',
                lastName: registrants[0]?.lastName ?? '',
              })
            }
            autoComplete="email"
          />
          <input
            className="field"
            type="tel"
            placeholder="Mobile phone"
            value={purchaser.phone}
            onChange={(e) => setPurchaser({ ...purchaser, phone: formatPhone(e.target.value) })}
            autoComplete="tel"
          />
          {minor && (
            <label className="text-slate block text-xs">
              Date of birth
              <input
                className="field mt-1"
                type="date"
                value={registrants[0]?.dateOfBirth ?? ''}
                onChange={(e) => update(registrants[0]?.key ?? '', { dateOfBirth: e.target.value })}
              />
            </label>
          )}
          <p className="text-slate text-xs">
            Have a Woodside account?{' '}
            <button type="button" className="text-navy font-semibold underline" onClick={signIn}>
              Sign in
            </button>{' '}
            and we will fill this in.
          </p>
        </section>
      )}

      {!prohibitGuests && (
        <section className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="kicker">{viewer ? 'Bringing someone else?' : 'Anyone else?'}</h3>
            <button type="button" className="chip" onClick={addGuest}>
              <Plus className="h-3 w-3" /> Add a guest
            </button>
          </div>
          {guests.slice(viewer ? 0 : 1).map((g) => (
            <div key={g.key} className="card space-y-2 p-3">
              <div className="flex items-center justify-between">
                <span className="text-slate text-xs font-bold">Guest</span>
                <button
                  type="button"
                  className="text-rose text-xs font-semibold"
                  onClick={() => removeGuest(g.key)}
                >
                  Remove
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input
                  className="field"
                  placeholder="First name"
                  value={g.firstName}
                  onChange={(e) => update(g.key, { firstName: e.target.value })}
                />
                <input
                  className="field"
                  placeholder="Last name"
                  value={g.lastName}
                  onChange={(e) => update(g.key, { lastName: e.target.value })}
                />
              </div>
              <input
                className="field"
                type="email"
                placeholder="Email (optional)"
                value={g.email}
                onChange={(e) => update(g.key, { email: e.target.value })}
              />
              {/* Always asked: with the name it finds a student who has no email. */}
              <label className="block space-y-1">
                <span className="text-slate text-xs font-semibold">Date of birth</span>
                <input
                  className="field"
                  type="date"
                  value={g.dateOfBirth}
                  onChange={(e) => update(g.key, { dateOfBirth: e.target.value })}
                />
              </label>
            </div>
          ))}
        </section>
      )}

      {/* No "Confirmation goes to" when signed in: it goes to the person
          signed in, at the email MP has for them (Colton, 2026-10-13). */}
    </div>
  );
}

function DetailsStep({
  registrant,
  image,
  subtitle,
  index = 0,
  count = 1,
  product,
  form,
  campuses,
  person,
  household = [],
  medicalTypes = [],
  update,
  answer,
  onLearn,
  onLearnGender,
  onSaveMedical,
  onAddMedical,
  onRemoveMedical,
  hideGroupIds = [],
  dress,
  address,
}: {
  registrant: RegistrantDraft | undefined;
  image?: string | null;
  /** The household member this is, when it is one: their record answers some of it. */
  person?: ClientPerson | null;
  /** Everyone else at their address, for the questions that name a person. */
  household?: ClientPerson[];
  /** MP's medical vocabulary, shared by everyone. */
  medicalTypes?: MedicalType[];
  /** What they are coming as. */
  subtitle?: string | null;
  /** Which of the party is being answered for, when it is more than one. */
  index?: number;
  count?: number;
  product: ClientProduct | null;
  form: ClientForm | null;
  campuses?: FormCampusOption[];
  update: (k: string, patch: Partial<RegistrantDraft>) => void;
  /** One answer at a time, merged as it lands. */
  answer: (k: string, id: number, value: string) => void;
  /** A fact about a record, on its way back to MP. */
  onLearn?: (fact: LearnedRelationship) => void;
  onLearnGender?: (contactId: number, gender: 'male' | 'female') => void;
  /** Named by contact id here; the step curries it onto the person it is asking. */
  onSaveMedical?: (contactId: number, item: MedicalItem, patch: MedicalDraft) => Promise<boolean>;
  onAddMedical?: (contactId: number, draft: MedicalDraft) => Promise<boolean>;
  onRemoveMedical?: (contactId: number, item: MedicalItem) => Promise<boolean>;
  /**
   * Option prices the registration step already chose. Their group is the
   * registration itself, and asking it again here would be the same question
   * twice with two different answers possible.
   */
  hideGroupIds?: number[];
  dress?: SheetDress;
  /** The household's mailing address, on the one person whose form asks for it. */
  address?: { value: MailingAddress; onChange: (next: MailingAddress) => void };
}) {
  if (!registrant) return null;
  const setOpt = (id: number, qty: number, group: ClientProduct['groups'][number]) => {
    const next = { ...registrant.options };
    if (group.mutuallyExclusive) for (const o of group.options) delete next[o.id];
    if (qty > 0) next[id] = qty;
    else delete next[id];
    update(registrant.key, { options: next });
  };
  const setAns = (id: number, v: string) => answer(registrant.key, id, v);

  const asked = (form?.fields ?? []).some(
    (f) => !f.parentId && !f.hidden && (form ? isVisible(form, f, registrant.answers) : true)
  );

  return (
    <div className="space-y-6">
      {/* Whose answers these are, the same way the registration step says it:
          a party of three is three near-identical forms, and the face is what
          stops somebody answering for the wrong child. */}
      <div className="flex items-center gap-3">
        <Avatar
          name={`${registrant.firstName} ${registrant.lastName}`.trim() || 'Guest'}
          image={image ?? null}
          size={40}
        />
        <div className="min-w-0 flex-1">
          <p className="text-navy text-base font-bold">
            A few questions for {registrant.firstName || 'this guest'}
          </p>
          <p className="text-slate truncate text-xs">
            {count > 1 ? `${index + 1} of ${count}` : null}
            {count > 1 && subtitle ? ' · ' : null}
            {subtitle}
          </p>
        </div>
      </div>

      {product?.groups
        .filter((g) => !g.options.some((o) => hideGroupIds.includes(o.id)))
        /*
         * A DISCOUNT IS NOT A QUESTION.
         *
         * MP files promo rows as an option group like any other, so the sheet
         * was drawing "Promo codes" as a list to tick — and a volunteer on a
         * free seat was being offered $2 off it. Both are the same mistake:
         * who gets what off is decided by the server against MP's rules, and
         * handing it to the person as a choice means either they tick the
         * discount somebody else's rule earned, or they leave theirs untaken.
         *
         * So the shelf is never drawn. What they qualify for is already on the
         * registration card they picked, and again on review.
         */
        .filter((g) => !isDiscountShelf(g))
        .map((g) => (
          <section key={g.id}>
            <h3 className="text-navy text-[0.92rem] font-bold">
              {dress?.groups?.[g.id]?.title ?? g.name}
              {g.required && <span className="text-rose ml-1">*</span>}
            </h3>
            {dress?.groups?.[g.id]?.hint ? (
              <p className="text-slate mb-2 text-xs">{dress.groups[g.id]!.hint}</p>
            ) : g.description && !dress?.groups?.[g.id]?.hideDescription ? (
              <p className="text-slate mb-2 text-xs">{g.description}</p>
            ) : null}
            <div className="mt-2 space-y-2">
              {g.options.map((o) => {
                const qty = registrant.options[o.id] ?? 0;
                const on = qty > 0;
                const canQty = !g.mutuallyExclusive && o.qtyAllowed != null && o.qtyAllowed > 1;
                const min = o.minQty && o.minQty > 0 ? o.minQty : 1;
                return (
                  <div
                    key={o.id}
                    className={`card flex items-center gap-3 p-3 transition ${on ? '!ring-teal !ring-2' : ''} ${o.soldOut ? 'opacity-50' : 'lift cursor-pointer'}`}
                    onClick={() => !o.soldOut && !canQty && setOpt(o.id, on ? 0 : min, g)}
                    role={canQty ? undefined : 'checkbox'}
                    aria-checked={on}
                  >
                    <span
                      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${on ? 'bg-teal text-white' : 'bg-mist'}`}
                    >
                      {on && <Check className="h-3.5 w-3.5" />}
                    </span>
                    {dress?.options?.[o.id] ? (
                      <Avatar
                        name={dress.options[o.id]!.subtitle ?? dress.options[o.id]!.title}
                        image={dress.options[o.id]!.imageUrl ?? null}
                        size={44}
                      />
                    ) : null}
                    <span className="min-w-0 flex-1">
                      <span className="text-navy block text-sm font-semibold">
                        {dress?.options?.[o.id]?.title ?? o.title}
                      </span>
                      {dress?.options?.[o.id]?.subtitle ? (
                        <span className="text-slate block text-xs">
                          {dress.options[o.id]!.subtitle}
                        </span>
                      ) : null}
                      {dress?.options?.[o.id]?.detail ? (
                        <span className="text-slate mt-0.5 block text-xs">
                          {dress.options[o.id]!.detail}
                        </span>
                      ) : null}
                      {o.soldOut ? (
                        <span className="text-rose text-xs font-bold">Sold out</span>
                      ) : o.left != null && o.left <= 10 ? (
                        <span className="text-amber text-xs font-semibold">{o.left} left</span>
                      ) : null}
                    </span>
                    {canQty && !o.soldOut && (
                      <span
                        className="border-line bg-paper inline-flex items-center gap-1 rounded-full border p-0.5"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          className="hover:bg-mist flex h-7 w-7 items-center justify-center rounded-full"
                          onClick={() => setOpt(o.id, qty - 1 < min ? 0 : qty - 1, g)}
                          aria-label="Fewer"
                        >
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                        <span className="w-6 text-center text-sm font-bold">{qty}</span>
                        <button
                          type="button"
                          className="hover:bg-mist flex h-7 w-7 items-center justify-center rounded-full"
                          onClick={() =>
                            setOpt(o.id, Math.min(o.qtyAllowed ?? 99, Math.max(min, qty + 1)), g)
                          }
                          aria-label="More"
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      </span>
                    )}
                    {o.price !== 0 && (
                      <span className="text-navy shrink-0 text-sm font-extrabold">
                        {(o.price < 0 ? '−' : '+') + fmtMoney(Math.abs(o.price))}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ))}

      {(asked || address) && (
        <section className="space-y-5">
          {address && <AddressFields value={address.value} onChange={address.onChange} />}
          {asked && (
            <Questions
              answerDress={dress?.answers}
              hideFields={dress?.hideFields}
              form={form}
              answers={registrant.answers}
              setAnswer={setAns}
              campuses={campuses}
              person={person ?? null}
              household={household}
              medicalTypes={medicalTypes}
              medical={registrant.medical ?? []}
              setMedical={(rows) => update(registrant.key, { medical: rows })}
              medicalNote={registrant.medicalNote ?? ''}
              setMedicalNote={(v) => update(registrant.key, { medicalNote: v })}
              registrantName={registrant.firstName || 'them'}
              /*
              Null when the reader is answering about themselves, so the
              questions keep saying "you". A guest with no name typed yet is
              also null: "What grade is in?" is worse than the second person.
            */
              aboutName={person?.isViewer ? null : registrant.firstName.trim() || null}
              /* Their own gender, never the viewer's, and never guessed from a
               name: a pronoun in front of somebody's family is worth getting
               right or leaving neutral. */
              aboutGender={person?.isViewer ? null : (person?.gender ?? null)}
              /* For choosing, so it is theirs whoever is answering. What MP
               holds wins over what was typed here. */
              personGender={person?.gender ?? registrant.gender ?? null}
              onPersonGender={(g) => {
                // Kept on the draft for a guest MP has no record of yet; saved
                // to the record straight away for anybody it does know.
                update(registrant.key, { gender: g });
                if (person) onLearnGender?.(person.contactId, g);
              }}
              onLearn={onLearn}
              /* Only somebody MP already knows has rows on file to correct. */
              onSaveMedical={
                person && onSaveMedical
                  ? (item, patch) => onSaveMedical(person.contactId, item, patch)
                  : undefined
              }
              onAddMedical={
                person && onAddMedical
                  ? (draft) => onAddMedical(person.contactId, draft)
                  : undefined
              }
              onRemoveMedical={
                person && onRemoveMedical
                  ? (item) => onRemoveMedical(person.contactId, item)
                  : undefined
              }
            />
          )}
        </section>
      )}
    </div>
  );
}

function ReviewStep({
  registrants,
  shiftsFor,
  seriesDates,
  photoOf,
  pricing,
  product,
  promo,
  promoInput,
  setPromoInput,
  applyPromo,
  clearPromo,
  promoError,
  paymentOption,
  setPaymentOption,
  pending,
  automatic,
  pricingNote,
  answersFor,
}: {
  registrants: RegistrantDraft[];
  /** What a volunteer signed up to do, in the words the shift list used. */
  shiftsFor: (r: RegistrantDraft) => string[];
  /** How many dates this one sign-up covers; 0 or 1 means just this one. */
  seriesDates: number;
  photoOf: (r: RegistrantDraft) => string | null;
  pricing: ReturnType<typeof priceRegistration>;
  product: ClientProduct | null;
  promo: PromoApplied | null;
  promoInput: string;
  setPromoInput: (s: string) => void;
  applyPromo: () => void;
  clearPromo: () => void;
  promoError: string | null;
  paymentOption: 'full' | 'deposit';
  setPaymentOption: (p: 'full' | 'deposit') => void;
  pending: boolean;
  automatic: Record<string, PromoLine[]>;
  pricingNote: string | null;
  /** Each person's answers, as they will be written. */
  answersFor: (r: RegistrantDraft) => ReviewAnswer[];
}) {
  /**
   * "Free" is what a discount did, not what a night costs. A registration
   * that never had a price says nothing at all where the money would be; one
   * that was brought down to nothing says so, because somebody expecting to
   * pay $10 needs to see where it went. Read off the lines themselves, which
   * is the only place that knows whether a discount actually landed — it does
   * not land on a seat that had no price to start with.
   */
  const discounted = (r: { items: { amount: number }[] }) => r.items.some((it) => it.amount < 0);
  const gifted = pricing.registrants.some(discounted);
  const paidFor = (r: { subtotal: number; items: { amount: number }[] }) =>
    r.subtotal > 0 ? fmtMoney(r.subtotal) : discounted(r) ? 'Free' : null;
  return (
    <div className="space-y-5">
      {pricingNote && (
        <p className="bg-amber-soft text-amber rounded-2xl px-4 py-2.5 text-sm font-semibold">
          {pricingNote}
        </p>
      )}
      {/* No card here: the sheet is already the card, and a panel inside a
          panel is a border drawn twice around the same thing. Rows, ruled. */}
      <section className="divide-mist divide-y">
        {pricing.registrants.map((r, i) => {
          const draft = registrants[i];
          return (
            <div key={draft?.key ?? i} className="flex items-start gap-3 py-4">
              <Avatar name={r.name} image={draft ? photoOf(draft) : null} size={38} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-navy truncate font-bold">{r.name}</span>
                  {product && paidFor(r) && (
                    <span className="text-navy shrink-0 font-extrabold">{paidFor(r)}</span>
                  )}
                </div>
                {draft && shiftsFor(draft).length > 0 && (
                  <ul className="text-slate mt-1 space-y-0.5 text-xs">
                    {shiftsFor(draft).map((title) => (
                      <li key={title}>{title}</li>
                    ))}
                  </ul>
                )}
                {r.items.length > 0 && (
                  <ul className="text-slate mt-1 space-y-0.5 text-xs">
                    {r.items.map((it, j) => (
                      <li key={j} className="flex justify-between gap-3">
                        <span className="min-w-0">{it.label}</span>
                        <span className="shrink-0">
                          {it.amount === 0
                            ? null
                            : it.amount < 0
                              ? `−${fmtMoney(-it.amount)}`
                              : fmtMoney(it.amount)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                {/* WHAT THEY SAID, WHERE THEY CAN STILL CHANGE IT. Review is
                    the last screen before money moves, and on some events an
                    answer IS a price — a shirt size, a meal, a bus seat. Even
                    where it is not, this is where somebody notices they put
                    the wrong grade in. */}
                {draft &&
                  (() => {
                    const said = answersFor(draft);
                    if (!said.length) return null;
                    /* Consecutive dependants are ONE branch, not one each:
                       three short rules stacked look like three unrelated
                       marks, and the point of the rule is that these belong
                       together under the answer above them. */
                    const rows: { row: ReviewAnswer; kids: ReviewAnswer[] }[] = [];
                    for (const a of said) {
                      const last = rows[rows.length - 1];
                      if (a.kind === 'text' && a.under && last) last.kids.push(a);
                      else rows.push({ row: a, kids: [] });
                    }
                    return (
                      <ul className="mt-2 space-y-0.5 text-xs">
                        {rows.map(({ row: a, kids }, j) => {
                          if (a.kind === 'emergency')
                            return (
                              <li key={j} className="pt-1.5">
                                {/* Who to call is a person, not three answers.
                                    Same card the question was asked with, so
                                    the last look at it matches the moment it
                                    was confirmed. */}
                                <span className="card flex items-center gap-2.5 p-2.5">
                                  <Avatar name={a.name} image={a.image} size={30} />
                                  <span className="min-w-0 flex-1">
                                    <span className="text-navy block truncate font-bold">
                                      {a.name}
                                    </span>
                                    <span className="text-slate block truncate">
                                      {[a.relationship, a.phone].filter(Boolean).join(' · ')}
                                    </span>
                                  </span>
                                  <span className="kicker shrink-0">Emergency</span>
                                </span>
                              </li>
                            );
                          // A question that only exists because of the answer
                          // above it is drawn as a branch off that answer: the
                          // rule says "these three are the Yes", which a flat
                          // list of five cannot.
                          return (
                            <li key={j} className="text-slate">
                              {a.label} <span className="text-navy font-semibold">{a.value}</span>
                              {kids.length > 0 && (
                                <ul className="border-line mt-0.5 ml-1 space-y-0.5 border-l pl-3">
                                  {kids.map((k, i) =>
                                    k.kind === 'text' ? (
                                      <li key={i} className="text-slate">
                                        {k.label}{' '}
                                        <span className="text-navy font-semibold">{k.value}</span>
                                      </li>
                                    ) : null
                                  )}
                                </ul>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    );
                  })()}
              </div>
            </div>
          );
        })}
        {seriesDates > 1 && (
          <div className="pt-4">
            <SeriesNote dates={seriesDates} />
          </div>
        )}
        {product && (pricing.total > 0 || gifted) && (
          <div className="flex items-center justify-between py-4">
            <span className="text-navy font-bold">Total</span>
            <span className="text-navy text-lg font-extrabold">
              {pricing.total > 0 ? fmtMoney(pricing.total) : 'Free'}
            </span>
          </div>
        )}
      </section>

      {/* Nothing left to discount, nothing to ask for. A code box under a $0
          total is a puzzle: it implies there is money here that the right
          word would remove. An already-applied code keeps its box, because
          that is also how it is taken off again. */}
      {product?.hasPromos && (pricing.total > 0 || promo) && (
        <section>
          <h3 className="kicker mb-2">Promo code</h3>
          {promo ? (
            <div className="bg-teal-soft flex items-center justify-between rounded-2xl px-4 py-3 text-sm">
              <span className="text-teal-deep font-bold">
                {promo.code} applied · {promo.title}
              </span>
              <button
                type="button"
                className="text-slate text-xs font-semibold"
                onClick={clearPromo}
              >
                Remove
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <input
                className="field uppercase"
                placeholder="Code"
                value={promoInput}
                onChange={(e) => setPromoInput(e.target.value)}
                /* Enter here means "apply this code", never "go to payment" —
                   marked so the sheet's own Enter handler leaves it alone, and
                   stopped so it cannot reach it either way. */
                data-enter="apply"
                onKeyDown={(e) => {
                  if (e.key !== 'Enter') return;
                  e.preventDefault();
                  e.stopPropagation();
                  applyPromo();
                }}
              />
              <button
                type="button"
                className="btn btn-quiet"
                onClick={applyPromo}
                disabled={pending || !promoInput.trim()}
              >
                Apply
              </button>
            </div>
          )}
          {promoError && <p className="text-rose mt-1 text-xs font-semibold">{promoError}</p>}
        </section>
      )}

      {pricing.depositAvailable && product?.depositPrice != null && (
        <section>
          <h3 className="kicker mb-2">Pay today</h3>
          <div className="grid grid-cols-2 gap-2">
            {(['full', 'deposit'] as const).map((p) => (
              <button
                key={p}
                type="button"
                className={`card p-3 text-left ${paymentOption === p ? '!ring-teal !ring-2' : 'lift'}`}
                onClick={() => setPaymentOption(p)}
              >
                <div className="text-navy text-sm font-bold">
                  {p === 'full' ? 'Pay in full' : 'Deposit'}
                </div>
                <div className="text-slate text-xs">
                  {p === 'full'
                    ? fmtMoney(pricing.total)
                    : `${fmtMoney((product.depositPrice ?? 0) * registrants.length)} now, rest later`}
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/*
       * Nothing to say when nothing is owed.
       *
       * It used to say "No payment needed. You will get a confirmation by
       * email" — the second half untrue, since nothing sends one yet, and
       * the first half already obvious from a review screen showing no
       * total. A line that is half wrong and half redundant is worse than
       * the space it takes.
       */}
      {pricing.amountDue > 0 && (
        <p className="text-slate text-xs">
          {popupPaymentsWanted()
            ? 'Payment opens in a secure window from Online Giving, our payment provider. Your spot is held while you pay.'
            : 'You will finish on our secure payment page. Your spot is held while you pay.'}
        </p>
      )}
    </div>
  );
}

/** "Sarah", "Sarah and Weston", "Sarah, Weston and Rosie". */
function DoneStep({
  result,
  faces,
  freeNames = [],
  heldNames = [],
  eventTitle,
  invoiceLink,
  onView,
  pay,
  onPayHere,
  onOpenAgain,
}: {
  result: Extract<SubmitResult, { ok: true }>;
  faces: { name: string; image: string | null }[];
  /** Registered outright: nothing about them is waiting on the money. */
  freeNames?: string[];
  /** Holding a seat until the invoice is paid. */
  heldNames?: string[];
  eventTitle: string;
  invoiceLink: (guid: string) => string;
  onView: () => void;
  pay: ReturnType<typeof usePaymentWindow>;
  onPayHere: () => void;
  onOpenAgain: () => void;
}) {
  const owed = result.amountDue > 0;
  const inWindow = pay.state.kind !== 'idle';
  return (
    <div className="rise space-y-4">
      <div className="card p-6 text-center">
        {/* The faces, then the check: who is in is the news. */}
        <div className="flex items-center justify-center -space-x-2">
          {faces.map((f, i) => (
            <span key={i} className="ring-paper rounded-full ring-2">
              <Avatar name={f.name} image={f.image} size={46} />
            </span>
          ))}
          <span className="bg-teal ring-paper relative flex h-7 w-7 items-center justify-center self-end rounded-full text-white ring-2">
            <Check className="h-4 w-4" strokeWidth={3} />
          </span>
        </div>
        <h3 className="text-navy mt-3 text-xl font-extrabold">
          {owed ? (freeNames.length ? 'Nearly there' : 'Your spot is held') : 'You are in'}
        </h3>
        {/* A party can end in two states at once: a volunteer who owes nothing
            is registered, and the student beside her is held until the card
            goes through. Saying "your spot is held" over both would be wrong
            about one of them. */}
        {owed && freeNames.length > 0 && heldNames.length > 0 ? (
          <p className="text-slate mt-1 text-sm">
            {listNames(freeNames)} {freeNames.length > 1 ? 'are' : 'is'} in. {listNames(heldNames)}{' '}
            {heldNames.length > 1 ? 'have seats' : 'has a seat'} held until you pay.
          </p>
        ) : (
          <p className="text-slate mt-1 text-sm">
            {result.participants.map((p) => p.name).join(', ')} for {eventTitle}.
          </p>
        )}
        {owed && result.paymentUrl && !inWindow && (
          <>
            <p className="text-slate mt-3 text-xs">
              Taking you to our secure payment page. If nothing happens, use the button.
            </p>
            {/* OLG's mpsso.js would rewrite this href on click if it could
                see it. It cannot — this link is rendered long after the
                script has run, inside a shadow root it does not search — so
                the token is on the href from the start. */}
            <a href={withMpSso(result.paymentUrl)} className="btn btn-primary mt-3 w-full">
              Pay {fmtMoney(result.amountDue)}
            </a>
            {/* `.btn` is `white-space: nowrap` and unlayered, so a sentence
                this long walked straight out of both ends of the pill on a
                phone. The `!` is what beats an unlayered rule; the line
                height goes with it, because `.btn` sets 1 and two wrapped
                lines at 1 touch. */}
            <a
              href={invoiceLink(result.invoiceGuid)}
              className="btn btn-quiet mt-2 w-full !py-3 !leading-snug !whitespace-normal"
            >
              Pay later, or send the link to whoever is paying
            </a>
          </>
        )}
        {!owed && (
          <button type="button" className="btn btn-quiet mt-4 w-full" onClick={onView}>
            View registration
          </button>
        )}
      </div>
      {owed && inWindow && (
        <PaymentPanel
          pay={pay}
          amount={result.amountDue}
          onPayHere={onPayHere}
          onOpenAgain={onOpenAgain}
          payLaterHref={invoiceLink(result.invoiceGuid)}
        />
      )}
      {owed && (
        <p className="text-slate text-center text-xs">
          Your seats are held for about three hours while you pay.
        </p>
      )}
    </div>
  );
}
